import { STAGE_H, STAGE_W, type RenderOp, type StageGeometry, type ResponseCode } from '@/domain/core/types';
import type { AssetCache } from './AssetCache';

/**
 * Canvas 렌더러.
 *
 * 논리 무대 1000×700 을 화면에 종횡비 유지 최대 내접 스케일로 놓는다.
 * 자극의 시각적 크기가 화면마다 달라지면 세션 간 비교가 깨지므로,
 * 물리 캘리브레이션(pxPerMm)이 있으면 mm 단위를 무대 단위로 환산한다.
 *
 * execute() 는 트라이얼 중 매 페이즈 진입 시 한 번 불린다. 여기서 할당하지 않는다.
 */
export interface RendererOptions {
  /** 캘리브레이션 결과. null 이면 논리 단위 기본값을 쓴다. */
  pxPerMm: number | null;
  background?: string;
  foreground?: string;
}

export class Renderer {
  readonly canvas: HTMLCanvasElement;
  #ctx: CanvasRenderingContext2D;
  #dpr = 1;
  #scale = 1;      // 무대 단위 → CSS px
  #offX = 0;
  #offY = 0;
  #bg: string;
  #fg: string;
  #pxPerMm: number | null;
  #lastCorrect: boolean | null = null;
  #lastResponse: ResponseCode | null = null;
  #feedbackAssetId = 'feedback-timeout';
  readonly desynchronized: boolean;

  constructor(canvas: HTMLCanvasElement, opts: RendererOptions) {
    this.canvas = canvas;
    this.#bg = opts.background ?? '#F7F9F8';
    this.#fg = opts.foreground ?? '#263E3B';
    this.#pxPerMm = opts.pxPerMm;

    // desynchronized:true — 저지연 경로. 일부 환경에서 티어링. 실제 적용 여부를 기록해 둔다.
    const ctx = canvas.getContext('2d', { alpha: false, desynchronized: true });
    if (!ctx) throw new Error('2D 컨텍스트를 만들 수 없습니다');
    this.#ctx = ctx;
    const attrs = (ctx as CanvasRenderingContext2D & { getContextAttributes?: () => { desynchronized?: boolean } }).getContextAttributes?.();
    this.desynchronized = attrs?.desynchronized === true;
    this.resize();
  }

  /** 캔버스 CSS 크기가 바뀌었을 때. 세션 중에는 부르지 않는다 (레이아웃 변화 = 데이터 오염). */
  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    this.#dpr = window.devicePixelRatio || 1;
    const w = Math.max(1, Math.round(rect.width * this.#dpr));
    const h = Math.max(1, Math.round(rect.height * this.#dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    this.#scale = Math.min(rect.width / STAGE_W, rect.height / STAGE_H);
    this.#offX = (rect.width - STAGE_W * this.#scale) / 2;
    this.#offY = (rect.height - STAGE_H * this.#scale) / 2;
    this.#ctx.setTransform(this.#dpr, 0, 0, this.#dpr, 0, 0);
    this.clear();
  }

  /** 무대 단위 1 이 디바이스 픽셀 몇 개인가. AssetCache 가 비트맵 해상도를 정할 때 쓴다. */
  get stageToDevicePx(): number { return this.#scale * this.#dpr; }

  /** mm → 무대 단위. 캘리브레이션이 없으면 null. */
  mmToStage(mm: number): number | null {
    if (this.#pxPerMm === null) return null;
    return (mm * this.#pxPerMm) / this.#scale;
  }

  /**
   * 프로토콜의 mm 명세를 무대 기하로 바꾼다.
   * 캘리브레이션이 없으면 논리 기본값(화면 높이의 비율)으로 떨어진다 — 그 사실은 세션에 기록되어야 한다.
   */
  geometryFor(separationMm: number, stimulusHeightMm: number): { geometry: StageGeometry; physical: boolean } {
    const sep = this.mmToStage(separationMm);
    const hgt = this.mmToStage(stimulusHeightMm);
    const physical = sep !== null && hgt !== null;
    const separation = sep ?? STAGE_H * 0.22;
    const stimulusHeight = hgt ?? STAGE_H * 0.055;
    return {
      physical,
      geometry: {
        centerX: STAGE_W / 2,
        centerY: STAGE_H / 2,
        separation,
        stimulusHeight,
        fixationSize: stimulusHeight * 0.9,
        probeSize: stimulusHeight * 0.5,
      },
    };
  }

  setLastResponse(response: ResponseCode | null, correct: boolean | null): void {
    this.#lastResponse = response;
    this.#lastCorrect = correct;
    this.#feedbackAssetId = response === null ? 'feedback-timeout' : `feedback-${correct ? 'correct' : 'incorrect'}-${response}`;
  }

  clear(): void {
    const c = this.#ctx;
    c.fillStyle = this.#bg;
    c.fillRect(0, 0, this.canvas.width / this.#dpr, this.canvas.height / this.#dpr);
  }

  /** 페이즈 진입 시 1회. 여기서는 할당하지 않는다. */
  execute(ops: readonly RenderOp[], assets: AssetCache): void {
    const c = this.#ctx;
    const s = this.#scale, ox = this.#offX, oy = this.#offY;
    for (let i = 0; i < ops.length; i += 1) {
      const op = ops[i] as RenderOp;
      switch (op.op) {
        case 'clear':
          this.clear();
          break;
        case 'fixation': {
          const x = ox + op.x * s, y = oy + op.y * s, h = (op.size * s) / 2, t = Math.max(1, op.size * s * 0.12);
          c.fillStyle = this.#fg;
          c.fillRect(x - t / 2, y - h, t, h * 2);
          c.fillRect(x - h, y - t / 2, h * 2, t);
          break;
        }
        case 'bitmap': {
          const a = assets.get(op.assetId);
          if (!a) break; // verify() 로 사전에 걸러졌어야 한다
          c.drawImage(a.bitmap, ox + (op.x - a.wStage / 2) * s, oy + (op.y - a.hStage / 2) * s, a.wStage * s, a.hStage * s);
          break;
        }
        case 'probe': {
          const x = ox + op.x * s, y = oy + op.y * s, r = Math.max(1.5, op.size * s * 0.18);
          c.fillStyle = this.#fg;
          if (op.kind === 'two-dot') {
            const gap = op.size * s * 0.45;
            c.beginPath(); c.arc(x - gap / 2, y, r, 0, Math.PI * 2); c.fill();
            c.beginPath(); c.arc(x + gap / 2, y, r, 0, Math.PI * 2); c.fill();
          } else {
            c.beginPath(); c.arc(x, y, r, 0, Math.PI * 2); c.fill();
          }
          break;
        }
        case 'feedback': {
          const x = ox + (STAGE_W / 2) * s, y = oy + (STAGE_H / 2 - 20) * s, r = 23 * s;
          c.strokeStyle = this.#lastCorrect ? '#3A805D' : '#B07742';
          c.lineWidth = Math.max(2, 3 * s);
          c.beginPath();
          if (this.#lastCorrect) {
            c.moveTo(x - r, y); c.lineTo(x - r * 0.3, y + r * 0.7); c.lineTo(x + r, y - r * 0.7);
          } else {
            c.moveTo(x - r, y - r); c.lineTo(x + r, y + r); c.moveTo(x + r, y - r); c.lineTo(x - r, y + r);
          }
          c.stroke();
          // Prepared bitmaps keep text layout out of the response loop.
          const label = assets.get(this.#feedbackAssetId);
          if (label) c.drawImage(label.bitmap, x - label.wStage * s / 2, y + 43 * s, label.wStage * s, label.hStage * s);
          if (this.#lastResponse) {
            c.fillStyle = this.#lastCorrect ? '#E3EEDF' : '#F7ECD9';
            const by = this.#lastResponse === 'top' ? oy + 40 * s : oy + (STAGE_H - 48) * s;
            c.fillRect(x - 80 * s, by, 160 * s, 8 * s);
          }
          break;
        }
        case 'text': {
          // 트라이얼 밖(지시문·휴식)에서만 쓴다. 자극에는 절대 쓰지 않는다.
          c.fillStyle = this.#fg;
          c.font = `${op.size * s}px "IBM Plex Sans KR", system-ui, sans-serif`;
          c.textAlign = 'center';
          c.textBaseline = 'middle';
          c.fillText(op.text, ox + op.x * s, oy + op.y * s);
          break;
        }
      }
    }
  }
}
